import { usePostHog } from '@/composables/usePostHog';
import { ENVIRONMENT } from '@/config';
import * as Sentry from '@sentry/vue';
import * as Pinia from 'pinia';

const { posthog } = usePostHog();

export const createException = (error: any, hint?: Parameters<typeof Sentry.captureException>[1]) => {
  posthog.startSessionRecording();
  const isLocalDevelopment = ENVIRONMENT !== 'staging' && ENVIRONMENT !== 'production';
  if (isLocalDevelopment) {
    console.error(error);
  }
  const pinia = Pinia.getActivePinia();
  if (pinia?.state?.value) Sentry.setContext('state', pinia.state.value);

  // Attach PostHog session recording URL to Sentry (The second condition is to exclude test environments)
  if (posthog && typeof posthog.get_session_replay_url === 'function') {
    const sessionReplayUrl = posthog.get_session_replay_url({ withTimestamp: true });
    if (sessionReplayUrl) {
      Sentry.setContext('posthog', {
        sessionReplayUrl,
        sessionId: posthog.get_session_id(),
      });
    }
  }

  // Api errors
  if (error?.response?.data) {
    if (error.response.status && error.response.status >= 400 && error.response.status < 500) {
      const message =
        typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data);
      Sentry.captureMessage(message, { level: 'warning', ...hint });
      return;
    }

    Sentry.captureException(error.response.data, hint);
    return;
  }
  // Other errors
  Sentry.captureException(error, hint);
};

export const createWarning = (error: any, context?: any) => {
  const isLocalDevelopment = ENVIRONMENT !== 'staging' && ENVIRONMENT !== 'production';
  if (isLocalDevelopment) {
    console.warn(error);
  }

  // Attach PostHog session recording URL to Sentry (The second condition is to exclude test environments)
  if (posthog && typeof posthog.get_session_replay_url === 'function') {
    const sessionReplayUrl = posthog.get_session_replay_url({ withTimestamp: true });
    if (sessionReplayUrl) {
      Sentry.setContext('posthog', {
        sessionReplayUrl,
        sessionId: posthog.get_session_id(),
      });
    }
  }

  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : JSON.stringify(error);
  Sentry.captureMessage(message, { level: 'warning', extra: context });
};

export const addBreadcrumb = (category: string, message: string) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level: 'info',
  });
};

export const addMetric = (type: string, tags: Record<string, any>) => {
  posthog.capture(type, tags);
  Sentry.metrics.count(type, 1, { attributes: tags });
};
